import { useNavigate, useParams } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'

import { observer } from 'mobx-react'

import Text from 'modules/core/components/Inputs/Text'
import TextArea from 'modules/core/components/Inputs/TextArea'
import Modal from 'modules/core/components/Modal'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { useDuplicateProcess } from 'modules/processes/hooks/useDuplicateProcess'
import { useProcess } from 'modules/processes/hooks/useProcess'

type Props = {
  processId: string
  onSuccess: (duplicatedProcessId: string) => void
  onClose: () => void
}

class DuplicateProcessForm extends Form {
  constructor() {
    super(
      {},
      {
        name: 'string',
        description: 'string',
      }
    )
  }
}

const DuplicateProcessModal = observer(({ processId, onSuccess, onClose }: Props) => {
  const { process } = useProcess(processId)
  const { duplicateProcess } = useDuplicateProcess()
  const form = useLazyRef(() => new DuplicateProcessForm()).current

  const { performAction, running: duplicatingProcess } = useAsyncAction(async () => {
    const { name, description } = form.values
    const { id } = await duplicateProcess(processId, name, description)

    onSuccess(id)
  })

  useUnsafeEffect(() => {
    if (process) {
      form.setValues({
        name: process.name,
        description: process.description,
      })
    }
  }, [form, process])

  const inputsDisabled = duplicatingProcess
  const submitDisabled = !form.get('name').hasValue || inputsDisabled

  return (
    <Modal
      illustration="add-block"
      title={i18n.t('processes.duplicate_process.title')}
      onSubmit={performAction}
      onClose={onClose}
      footer={
        <Button disabled={submitDisabled} submit>
          {i18n.t('common.submit')}
        </Button>
      }
    >
      <Box gap="s16">
        <Text
          mandatory
          readonly={inputsDisabled}
          label={i18n.t('processes.duplicate_process.name')}
          field={form.get('name')}
        />
        <TextArea
          readonly={inputsDisabled}
          field={form.get('description')}
          label={i18n.t('processes.duplicate_process.description')}
        />
      </Box>
    </Modal>
  )
})

const DuplicateProcessModalPage = () => {
  const { category, id } = useParams()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const onClose = () => {
    navigate(-1)
  }

  const onSuccess = (processId: string) => {
    navigate(
      navigation.processes.template.id.settings.fullPath({
        category: category as string,
        id: processId,
      }),
      {
        replace: true,
      }
    )
  }

  if (!id) {
    onClose()
    return null
  }

  return <DuplicateProcessModal processId={String(id)} onClose={onClose} onSuccess={onSuccess} />
}

export default DuplicateProcessModalPage
